import {
  add, addMinutes, Day, differenceInMinutes, endOfDay, endOfMonth, format, getDate,
  getDay as _getDay, getDaysInMonth, getMonth, getWeekOfMonth, getWeeksInMonth, lastDayOfMonth,
  nextDay, previousDay, setDate, startOfMonth
} from 'date-fns';

import { CalendarEventInput } from 'generated/graphql';

import { isSameDayLocal } from './is-same-day-local';
import { isoToDisplayDate } from './timezone-conversion';
import { fromZonedTime } from 'date-fns-tz';

interface EventCopy {
  label: string;
  selected: boolean;
  eventDetails: CalendarEventInput;
}

function formatEventLabel(startDate, endDate) {
  return !endDate ?
    format(startDate, `EEEE, MMMM d, y 'at' h:mmaaa`)
    : isSameDayLocal(startDate, endDate) ?
      format(startDate, `EEEE, MMMM d, y, h:mmaaa - `) + format(endDate, `h:mmaaa`)
      : format(startDate, `eee, MMM d, y, h:mmaaa - `) + format(endDate, `eee, MMM d, y, h:mmaaa`);
}

function getEventCopy(originalEvent: CalendarEventInput, nextDate: Date, endTimeDiff): EventCopy {
  const nextDateEnd = !endTimeDiff ? null : addMinutes(nextDate, endTimeDiff);
  return ({
    label: formatEventLabel(nextDate, nextDateEnd),
    selected: true,
    eventDetails: {
      ...originalEvent,
      startDate: fromZonedTime(nextDate, originalEvent.timeZone).toISOString(),
      endDate: !nextDateEnd ? null : fromZonedTime(nextDateEnd, originalEvent.timeZone).toISOString()
    }
  });
}

// Bad type definitions... This should probably return Day but the type returns number
function getDay(date: string | number | Date): Day {
  return _getDay(date) as Day;
}

export const orderSelectedDays = (originalDayOfWeek: Day, selectedDays: Array<Day>): Array<Day> => {
  selectedDays = selectedDays.sort();
  const originalDayIndex = selectedDays.indexOf(originalDayOfWeek);
  return selectedDays.slice(originalDayIndex).concat(selectedDays.slice(0, originalDayIndex));
};

export function generateMultipleEvents(
  originalEvent: CalendarEventInput,
  hoursSelections: Array<string>,
  interval: number,
  intervalType: 'day' | 'week' | 'month' | 'year',
  selectedDays: Array<Day>,
  monthIntervalType: 'date' | 'lastDay' | 'week',
  endDate: Date,
  maxEvents: number): Array<EventCopy> {

  const originalStartDate = isoToDisplayDate(originalEvent.startDate, originalEvent.timeZone);
  const originalEndDate = originalEvent.endDate ? isoToDisplayDate(originalEvent.endDate, originalEvent.timeZone) : null;
  const originalDate = getDate(originalStartDate);
  const originalDayOfWeek = getDay(originalStartDate);
  const endTimeDiff = !originalEndDate ? 0 : differenceInMinutes(originalEndDate, originalStartDate);
  const weekNumber = getWeekOfMonth(originalStartDate, { weekStartsOn: getDay(startOfMonth(originalStartDate)) });
  const isLastWeekOfMonth = getWeekOfMonth(originalStartDate) === getWeeksInMonth(originalStartDate);

  // Order selected days starting with original day
  selectedDays = orderSelectedDays(originalDayOfWeek, selectedDays);

  const eventCopies = [];
  let hourIndex = 0;
  let dayIndex = 0;
  let nextDate = originalStartDate;

  // Generate events until endDate or maxEvents + 1 is reached
  // (maxEvents + 1 so caller can check if more than max events would be created)
  while (eventCopies.length <= maxEvents + 1 && nextDate <= endOfDay(endDate)) {
    // Set start time
    const [hour, minute] = hoursSelections[hourIndex].split(':').map(Number);
    nextDate.setHours(hour, minute);

    eventCopies.push(getEventCopy(originalEvent, nextDate, endTimeDiff));

    // Set next event
    hourIndex++;
    if (hourIndex < hoursSelections.length) {
      // Next hour in same day
      continue;
    }

    // Next day/week/month/year
    hourIndex = 0;

    switch (intervalType) {
      case 'day':
        nextDate = add(nextDate, { days: interval });
        break;
      case 'week':
        // Next day in selected days
        dayIndex++;
        if (dayIndex > selectedDays.length - 1) {
          // Add week interval
          dayIndex = 0;
          nextDate = add(nextDate, { weeks: interval - 1 });
        }
        nextDate = nextDay(nextDate, selectedDays[dayIndex]);
        break;
      case 'month':
        nextDate = getNextEventByMonthInterval(nextDate, interval, monthIntervalType,
          originalDate, originalDayOfWeek, weekNumber, isLastWeekOfMonth);
        break;
      case 'year':
        // In case of Feb 29, the last day in Feb will always be selected
        nextDate = getMonth(originalStartDate) === 1 && originalDate === 29 ?
          nextDate = lastDayOfMonth(add(nextDate, { years: interval }))
          : nextDate = add(nextDate, { years: interval });
    }
  }
  return eventCopies;
}

function getNextEventByMonthInterval(
  prevDate: Date,
  interval: number,
  monthIntervalType: 'date' | 'lastDay' | 'week',
  originalDate: number,
  originalDayOfWeek: Day,
  weekNumber: number,
  isLastWeekOfMonth: boolean): Date {

  // Get next month based on interval
  const nextMonth = add(prevDate, { months: interval });

  switch (monthIntervalType) {
    case 'lastDay':
      return endOfMonth(nextMonth);
    case 'date':
      // On same day of month - may not exist (i.e. Feb 30) so move to last day
      return getDaysInMonth(nextMonth) < originalDate ?
        endOfMonth(nextMonth)
        : setDate(nextMonth, originalDate);
    case 'week':
      // On same week of month, same day of week (i.e. second Saturday)
      const monthStart = startOfMonth(nextMonth);
      const monthEnd = endOfMonth(nextMonth);
      // Start with first occurence of day in month
      let nextDate = getDay(monthStart) === originalDayOfWeek ? monthStart : nextDay(monthStart, originalDayOfWeek);
      // Get last occurence of day in month
      const lastOccurence = getDay(monthEnd) === originalDayOfWeek ? monthEnd : previousDay(monthEnd, originalDayOfWeek);
      if (isLastWeekOfMonth) {
        // Special case for last week of month, some months may have more weeks than the original event month
        return lastOccurence;
      }
      // Go through each week until target week number or last occurence of day in month
      while (getWeekOfMonth(nextDate, { weekStartsOn: getDay(monthStart) }) < weekNumber
        && !isSameDayLocal(nextDate, lastOccurence)) {
        nextDate = nextDay(nextDate, originalDayOfWeek);
      }
      return nextDate;
  }
}
